// Drilling-template implementations dispatched by `DrillingTemplate.kind`.
//
// Each template reads constants out of the part's evaluation scope (the
// class params block exposes SP_PITCH, SP_EDGE, HC_DIAM, … in inches as
// expressions) so a designer can author a class that switches to a 5mm
// shelf-pin row at 50mm pitch by just bumping params.
//
// Coordinate frame on the part's drilled face: X is the part's `cut.w`,
// Y is `cut.h`. The face (`"inside"` for side panels, `"back"` for door
// fronts) is inferred from the part role.

import type {
  CabinetPart,
  DrillingTemplate,
  PartDef,
  PartRole,
} from "@/lib/types";
import type { DrillEntry } from "..";
import { type Scope, evalValue } from "./expr";

export interface DrillContext {
  /** The evaluated part — `cut.w` and `cut.h` define the drilling frame. */
  part: CabinetPart;
  /** Symbol table at this part's level (class params + cabinet params + part params). */
  scope: Scope;
  /** Original PartDef — for role / joinery introspection. */
  partDef: PartDef;
}

const DOOR_ROLES: PartRole[] = ["door", "door_left", "door_right"];
const SIDE_ROLES: PartRole[] = ["side_left", "side_right"];

/** Read a number from a `DrillingTemplate` field — accepts string
 *  expressions or literal numbers, with a default if absent. */
function param(t: DrillingTemplate, key: string, def: string | number, scope: Scope): number {
  const v = t[key];
  if (v === undefined || v === null) return evalValue(def, scope);
  if (typeof v === "string" || typeof v === "number") return evalValue(v, scope);
  throw new Error(`drilling param "${key}" must be number or expression`);
}

function readSide(t: DrillingTemplate): "left" | "right" {
  const s = t.hingeSide;
  if (s === "right") return "right";
  return "left";
}

// ── Shelf-pin rows (32mm system) ─────────────────────────────────

function shelfpinRows(ctx: DrillContext, t: DrillingTemplate): DrillEntry[] {
  const { part, scope } = ctx;
  if (!SIDE_ROLES.includes(ctx.partDef.role)) return [];

  const SP_PITCH = evalValue("SP_PITCH", scope);
  const SP_EDGE  = evalValue("SP_EDGE",  scope);
  const SP_DIAM  = evalValue("SP_DIAM",  scope);
  const SP_DEPTH = evalValue("SP_DEPTH", scope);

  const SP_FRONT_OFF = param(t, "frontOff", "SP_FRONT_OFF", scope);
  const SP_BACK_OFF  = param(t, "backOff",  "SP_BACK_OFF",  scope);

  const partDepth  = part.w;
  const partHeight = part.h;

  const out: DrillEntry[] = [];
  const yMax = partHeight - SP_EDGE;
  const xs = [SP_BACK_OFF, partDepth - SP_FRONT_OFF];
  for (const x of xs) {
    if (x < SP_EDGE || x > partDepth - SP_EDGE) continue;
    for (let y = SP_EDGE; y <= yMax + 1e-6; y += SP_PITCH) {
      out.push({
        x, y,
        diameter: SP_DIAM, depth: SP_DEPTH,
        face: "inside", kind: "shelf_pin",
      });
    }
  }
  return out;
}

// ── Hinge cups (35mm Euro) ───────────────────────────────────────

function hingeCups(ctx: DrillContext, t: DrillingTemplate): DrillEntry[] {
  const { part, scope } = ctx;
  if (!DOOR_ROLES.includes(ctx.partDef.role)) return [];

  const HC_DIAM  = evalValue("HC_DIAM",  scope);
  const HC_DEPTH = evalValue("HC_DEPTH", scope);
  const HC_EDGE  = evalValue("HC_EDGE",  scope);
  const HC_TOP_BOTTOM = evalValue("HC_TOP_BOTTOM", scope);

  const doorW = part.w;
  const doorH = part.h;
  const side  = readSide(t);
  const x     = side === "left" ? HC_EDGE : (doorW - HC_EDGE);

  const ys = [HC_TOP_BOTTOM, doorH - HC_TOP_BOTTOM];
  if (doorH > 36) ys.push(doorH / 2);
  return ys.map((y) => ({
    x, y,
    diameter: HC_DIAM, depth: HC_DEPTH,
    face: "back" as const, kind: "hinge_cup" as const,
  }));
}

// ── Back-panel rebate groove ─────────────────────────────────────
//
// Vertical slot routed into the inside face of a side panel, running
// floor-to-ceiling along the back edge. Holds the back panel.
// Template params (with sensible defaults):
//   depth   — how deep the slot is cut into the panel  (default `BACK_T`)
//   width   — slot width (= back-panel thickness)      (default `BACK_T`)
//   offset  — distance of the slot's CENTRE from the back edge of the
//             part (back edge is X=0 on the inside face of a side
//             panel). Default = `width/2` so the slot's back wall is
//             flush with the part's back edge.
//
// Emits one DrillEntry with kind="groove" — start at (offset, 0),
// end at (offset, partHeight). DrillEntry.diameter carries the slot
// width for downstream tooling.

function grooveBack(ctx: DrillContext, t: DrillingTemplate): DrillEntry[] {
  const { part, scope } = ctx;
  if (!SIDE_ROLES.includes(ctx.partDef.role)) return [];
  const width  = param(t, "width",  "BACK_T", scope);
  const depth  = param(t, "depth",  "BACK_T", scope);
  const offset = param(t, "offset", String(width / 2), scope);
  return [{
    x: offset, y: 0,
    endX: offset, endY: part.h,
    diameter: width, depth,
    face: "inside", kind: "groove",
  }];
}

// ── Screw pattern (used by joinery-driven drilling) ──────────────

function screwPattern(ctx: DrillContext, t: DrillingTemplate): DrillEntry[] {
  const { part, scope } = ctx;
  const count   = Math.max(2, Math.round(param(t, "count",   2,    scope)));
  const margin  = param(t, "margin", 1, scope);
  const diam    = param(t, "diameter", "5/25.4", scope);
  const depth   = param(t, "depth",    "T",       scope);
  const yOf     = (t["y"] !== undefined) ? param(t, "y", 0, scope) : null;
  const faceVal = t.face;
  const face: "inside" | "back" = faceVal === "back" ? "back" : "inside";

  const span = (part.w) - 2 * margin;
  if (count === 1 || span <= 0) {
    return [{
      x: (part.w) / 2,
      y: yOf ?? (part.h) / 2,
      diameter: diam, depth, face, kind: "screw",
    }];
  }
  const out: DrillEntry[] = [];
  for (let i = 0; i < count; i++) {
    const x = margin + span * (i / (count - 1));
    out.push({
      x,
      y: yOf ?? (part.h) / 2,
      diameter: diam, depth, face, kind: "screw",
    });
  }
  return out;
}

// ── Public entry point ──────────────────────────────────────────

export function emitDrillings(
  ctx: DrillContext,
  templates: DrillingTemplate[] | undefined,
): DrillEntry[] {
  if (!templates?.length) return [];
  const out: DrillEntry[] = [];
  for (const t of templates) {
    switch (t.kind) {
      case "shelfpin_rows": out.push(...shelfpinRows(ctx, t)); break;
      case "hinge_cups":    out.push(...hingeCups(ctx, t));   break;
      case "screw_pattern": out.push(...screwPattern(ctx, t)); break;
      case "groove_back":   out.push(...grooveBack(ctx, t));   break;
      case "custom":
        // No built-in implementation; classes can declare custom holes
        // via extraDrillings on PartOverride instead.
        break;
    }
  }
  return out;
}
